<template>
  <Teleport to="body">
    <v-dialog
      :model-value="modelValue"
      max-width="440"
      persistent
      class="caller-dialog-root"
      @update:model-value="$emit('update:modelValue', $event)"
    >
      <SnackbarComp :SnackbarComponent="snackbar" />

      <div class="caller-dialog caller-dialog--danger" role="dialog" aria-modal="true">
        <header class="caller-dialog__header">
          <div class="caller-dialog__brand">
            <div class="caller-dialog__icon caller-dialog__icon--danger" aria-hidden="true">
              <v-icon icon="mdi-trash-can-outline" size="22" color="white" />
            </div>
            <div>
              <h2 class="caller-dialog__title">Delete caller</h2>
              <p class="caller-dialog__subtitle">
                This action cannot be undone.
              </p>
            </div>
          </div>
          <button
            type="button"
            class="caller-dialog__close"
            aria-label="Close"
            :disabled="saving"
            @click="close"
          >
            <v-icon icon="mdi-close" size="20" />
          </button>
        </header>

        <div class="caller-dialog__body caller-dialog__body--center">
          <p class="caller-dialog__confirm">
            Are you sure you want to remove
            <strong>{{ callerName }}</strong>
            from your caller list?
          </p>
          <p v-if="caller?.caller_id" class="caller-dialog__meta">
            {{ caller.caller_id_display || caller.caller_id }}
          </p>
        </div>

        <footer class="caller-dialog__footer">
          <v-btn
            variant="text"
            class="caller-dialog__cancel"
            :disabled="saving"
            @click="close"
          >
            Cancel
          </v-btn>
          <v-btn
            variant="flat"
            class="caller-dialog__submit caller-dialog__submit--danger"
            :loading="saving"
            @click="submit"
          >
            Delete caller
          </v-btn>
        </footer>
      </div>
    </v-dialog>
  </Teleport>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import SnackbarComp from '@/components/Extras/SnackbarComp.vue'
import { generateClient } from 'aws-amplify/api'
import { removeOrgCaller } from '@/graphql/mutations'
import { parseAwsJson } from '@/utils/graphql'
import { getApiMessage, isApiSuccess } from '@/mixins/Extras/ApiResponse'
import { displayCallerName } from '@/composables/callers/useCallersData'

defineOptions({ name: 'DeleteCallerDialog' })

const client = generateClient()

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  caller: { type: Object, default: null },
})

const emit = defineEmits(['update:modelValue', 'deleted'])

const store = useStore()
const saving = ref(false)
const snackbar = ref({})

const callerName = computed(() => displayCallerName(props.caller?.caller_name))

function close() {
  emit('update:modelValue', false)
}

async function submit() {
  const { org_id } = store.getters.sessionContext ?? {}
  if (!org_id || !props.caller?.caller_id) return

  saving.value = true
  try {
    const { data, errors } = await client.graphql({
      query: removeOrgCaller,
      variables: {
        input: {
          org_id,
          caller_id: String(props.caller.caller_id || '').trim(),
        },
      },
    })

    if (errors?.length) {
      throw new Error(errors[0].message)
    }

    const response = parseAwsJson(data?.removeOrgCaller)

    if (!isApiSuccess(response)) {
      snackbar.value = {
        SnackbarVmodel: true,
        SnackbarColor: 'error',
        SnackbarText: getApiMessage(response, 'Something went wrong'),
      }
      return
    }

    snackbar.value = {
      SnackbarVmodel: true,
      SnackbarColor: 'success',
      SnackbarText: 'Caller deleted successfully',
    }
    emit('deleted')
    close()
  } catch (err) {
    snackbar.value = {
      SnackbarVmodel: true,
      SnackbarColor: 'error',
      SnackbarText: getApiMessage(err, 'Something went wrong'),
    }
  } finally {
    saving.value = false
  }
}
</script>
